import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Container,
  Grid,
  TextField,
  Typography,
  Chip,
  InputAdornment,
  CircularProgress,
  Alert,
  Skeleton
} from '@mui/material';
import {
  Search as SearchIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  CheckCircle as JoinedIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSafeBack } from '../hooks/useSafeBack';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import teamService from '../services/teamService';
import departmentService from '../services/departmentService';

interface Team {
  id: number;
  name: string;
  description?: string;
  department?: string;
  departmentName?: string;
  memberCount?: number;
  maxMembers?: number;
  organizationName?: string;
  isPublic?: boolean;
  hasJoined?: boolean;
  hasPendingRequest?: boolean;
  membershipStatus?: string;
}

interface Department {
  id: number;
  name: string;
}

const JoinGroupPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [joinedTeams, setJoinedTeams] = useState<Team[]>([]);
  const [pendingTeams, setPendingTeams] = useState<Team[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState<number | null>(null);
  const [joiningTeam, setJoiningTeam] = useState<number | null>(null);

  useEffect(() => {
    // Redirect providers to teams page instead
    if (user?.userType === 'provider' || user?.userType === 'admin') {
      navigate('/teams');
      return;
    }
    loadData();
  }, [user, navigate]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadData(searchTerm, selectedDepartment || undefined);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedDepartment]);

  const loadData = async (searchQuery?: string, deptFilter?: number) => {
    try {
      setLoading(true);
      
      // Use search endpoint if we have search criteria, otherwise get available teams
      let availableResponse;
      if (searchQuery || deptFilter) {
        availableResponse = await teamService.searchTeams(searchQuery, deptFilter);
      } else {
        availableResponse = await teamService.getAvailableTeams();
      }
      
      const [joinedResponse, pendingResponse, deptResponse] = await Promise.all([
        teamService.getJoinedTeams(),
        teamService.getPendingRequests(),
        departmentService.getDepartments()
      ]);

      // Service methods now consistently return data directly
      const availableTeams = Array.isArray(availableResponse) ? availableResponse : [];
      const joinedTeams = Array.isArray(joinedResponse) ? joinedResponse : [];
      const pendingTeams = Array.isArray(pendingResponse) ? pendingResponse : [];
      const departments = Array.isArray(deptResponse) ? deptResponse : [];

      const joinedIds = new Set(joinedTeams.map((t: Team) => t.id));
      const pendingIds = new Set(pendingTeams.map((t: Team) => t.id));

      // Mark teams that are already joined or have pending requests and normalize data structure
      const teamsWithStatus = availableTeams.map((team: Team) => ({
        ...team,
        description: team.description || 'No description available',
        department: team.departmentName || team.department || 'General',
        memberCount: team.memberCount || 0,
        maxMembers: team.maxMembers || 50,
        organizationName: team.organizationName || '',
        hasJoined: joinedIds.has(team.id) || team.membershipStatus === 'approved',
        hasPendingRequest: pendingIds.has(team.id) || team.membershipStatus === 'pending'
      }));

      setTeams(teamsWithStatus);
      setJoinedTeams(joinedTeams);
      setPendingTeams(pendingTeams);
      setDepartments(departments);
    } catch (error: any) {
      console.error('Error loading teams:', error);
      const message = error.response?.data?.message || error.message || 'Failed to load teams';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinTeam = async (teamId: number) => {
    try {
      setJoiningTeam(teamId);
      await teamService.joinTeam(teamId);
      toast.success('Join request submitted successfully!');
      await loadData(); // Reload to update status
    } catch (error: any) {
      console.error('Error joining team:', error);
      toast.error(error.response?.data?.message || 'Failed to join team');
    } finally {
      setJoiningTeam(null);
    }
  };

  const handleCancelRequest = async (teamId: number) => {
    try {
      setJoiningTeam(teamId);
      await teamService.cancelJoinRequest(teamId);
      toast.success('Join request cancelled');
      await loadData();
    } catch (error) {
      console.error('Error cancelling request:', error);
      toast.error('Failed to cancel request');
    } finally {
      setJoiningTeam(null);
    }
  };

  // Teams are already filtered by server-side search
  const filteredTeams = teams;

  const getTeamStatus = (team: Team) => {
    if (team.hasJoined) return 'joined';
    if (team.hasPendingRequest) return 'pending';
    if ((team.memberCount || 0) >= (team.maxMembers || 0)) return 'full';
    return 'available';
  };

  const renderTeamCard = (team: Team) => {
    const status = getTeamStatus(team);
    const isJoining = joiningTeam === team.id;

    return (
      <Card key={team.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Typography variant="h6" component="h2">
              {team.name}
            </Typography>
            {status === 'joined' && (
              <Chip 
                icon={<JoinedIcon />} 
                label="Joined" 
                color="success" 
                size="small" 
              />
            )}
            {status === 'pending' && (
              <Chip 
                label="Pending" 
                color="warning" 
                size="small" 
              />
            )}
            {status === 'full' && (
              <Chip 
                label="Full" 
                color="error" 
                size="small" 
              />
            )}
          </Box>

          <Typography variant="body2" color="text.secondary" paragraph>
            {team.description || 'No description available'}
          </Typography>

          <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <SchoolIcon fontSize="small" color="action" />
              <Typography variant="body2">
                {team.department || 'General'}
              </Typography>
            </Box>
            
            <Box display="flex" alignItems="center" gap={1}>
              <PersonIcon fontSize="small" color="action" />
              <Typography variant="body2">
                {team.memberCount} / {team.maxMembers} members
              </Typography>
            </Box>
            
            {team.organizationName && (
              <Box display="flex" alignItems="center" gap={1}>
                <GroupIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  {team.organizationName}
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>

        <CardActions>
          {status === 'joined' && (
            <Button 
              size="small" 
              disabled
              fullWidth
            >
              Already Joined
            </Button>
          )}
          {status === 'pending' && (
            <Button 
              size="small" 
              color="warning"
              onClick={() => handleCancelRequest(team.id)}
              disabled={isJoining}
              fullWidth
            >
              {isJoining ? <CircularProgress size={20} /> : 'Cancel Request'}
            </Button>
          )}
          {status === 'full' && (
            <Button 
              size="small" 
              disabled
              fullWidth
            >
              Team Full
            </Button>
          )}
          {status === 'available' && (
            <Button 
              size="small" 
              variant="contained"
              color="primary"
              onClick={() => handleJoinTeam(team.id)}
              disabled={isJoining}
              fullWidth
            >
              {isJoining ? <CircularProgress size={20} /> : 'Request to Join'}
            </Button>
          )}
        </CardActions>
      </Card>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/groups')}
            size="small"
          >
            ← Back to Groups
          </Button>
          <Typography variant="h4" component="h1">
            Join a Group
          </Typography>
        </Box>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/groups/my-groups')}
        >
          My Groups
        </Button>
      </Box>

      <Box mb={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              placeholder="Search groups by name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Department"
              value={selectedDepartment || ''}
              onChange={(e) => setSelectedDepartment(e.target.value ? Number(e.target.value) : null)}
              SelectProps={{
                native: true,
              }}
              InputLabelProps={{
                shrink: true,
              }}
            >
              <option value="">All Departments</option>
              {departments.map((dept) => (
                <option key={dept.id} value={dept.id}>
                  {dept.name}
                </option>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Box>

      {loading ? (
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((n) => (
            <Grid item xs={12} sm={6} md={4} key={n}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" width="60%" height={32} />
                  <Skeleton variant="text" width="100%" height={60} />
                  <Skeleton variant="text" width="40%" />
                  <Skeleton variant="text" width="50%" />
                </CardContent>
                <CardActions>
                  <Skeleton variant="rectangular" width="100%" height={36} />
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : filteredTeams.length === 0 ? (
        <Alert severity="info">
          {searchTerm || selectedDepartment 
            ? 'No groups found matching your search criteria.'
            : 'No groups available to join at this time.'}
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {filteredTeams.map((team) => (
            <Grid item xs={12} sm={6} md={4} key={team.id}>
              {renderTeamCard(team)}
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default JoinGroupPage;